import type { AuthResponseData } from '../api/types'
import { ERROR_MESSAGES, getReasonMessage } from './errorMessages'

const REASON_CODES = {
  FIRST_STEP_VALIDATION: 'first_step_validation',
} as const

function getMessageOrMappedReason({
  reason,
  message,
}: {
  reason?: string
  message?: string
}): string | undefined {
  if (message) return message
  if (reason) {
    const reasonMessage = getReasonMessage(reason)
    if (reasonMessage) return reasonMessage
  }
  return undefined
}

export interface ErrorMappingOptions {
  statusCode?: number
  reason?: string
  message?: string
  redirectToErrorPage?: boolean
  accountAlreadyExists?: boolean
}

function getMessageByStatus(
  statusCode: number | undefined,
  statusMap: Partial<Record<number, string>>,
  defaultMessage: string
): string {
  const message = statusCode !== undefined ? statusMap[statusCode] : undefined
  return message ?? defaultMessage
}

export function getSignInErrorMessage(options: ErrorMappingOptions): string | null {
  const { statusCode, reason, message, redirectToErrorPage } = options

  if (redirectToErrorPage && statusCode === 429) {
    return null
  }

  const backendMessage = getMessageOrMappedReason({ reason, message })
  if (backendMessage) return backendMessage

  if (statusCode === 400 && options.accountAlreadyExists) {
    return ERROR_MESSAGES.login.email.server.accountExists
  }
  return getMessageByStatus(
    statusCode,
    {
      400: ERROR_MESSAGES.login.email.server.invalid,
      429: ERROR_MESSAGES.login.email.server.tooManyAttempts,
    },
    ERROR_MESSAGES.generic.default
  )
}

export function getSignInPasswordErrorMessage(options: ErrorMappingOptions): string {
  const { statusCode, reason, message } = options

  const backendMessage = getMessageOrMappedReason({ reason, message })
  if (backendMessage) return backendMessage

  return getMessageByStatus(
    statusCode,
    {
      400: ERROR_MESSAGES.login.password.server.incorrect,
      429: ERROR_MESSAGES.login.password.server.tooManyAttempts,
    },
    ERROR_MESSAGES.login.password.server.incorrectFallback
  )
}

export function getSignupDetailsErrorMessage(
  options: ErrorMappingOptions & { minPasswordLength?: number }
): string {
  const { reason, message } = options

  if (reason === REASON_CODES.FIRST_STEP_VALIDATION) {
    return message || ERROR_MESSAGES.signup.details.server.allFieldsRequired
  }

  const backendMessage = getMessageOrMappedReason({ reason, message })
  if (backendMessage) return backendMessage

  return ERROR_MESSAGES.signup.details.server.generic
}

export function getPasswordRecoveryErrorMessage(options: ErrorMappingOptions): string {
  const { statusCode, reason, message } = options

  const backendMessage = getMessageOrMappedReason({ reason, message })
  if (backendMessage) return backendMessage

  return getMessageByStatus(
    statusCode,
    {
      400: ERROR_MESSAGES.passwordRecovery.email.server.invalid,
      403: ERROR_MESSAGES.passwordRecovery.email.server.csrf,
      429: ERROR_MESSAGES.passwordRecovery.email.server.tooManyAttempts,
    },
    ERROR_MESSAGES.generic.default
  )
}

export function getPasswordResetErrorMessage(options: ErrorMappingOptions): string {
  const { reason, message } = options

  const backendMessage = getMessageOrMappedReason({ reason, message })
  if (backendMessage) return backendMessage

  return ERROR_MESSAGES.passwordRecovery.reset.server.generic
}

export type AuthErrorContext =
  | 'login-email'
  | 'login-password'
  | 'signup-details'
  | 'password-recovery'
  | 'password-reset'

/**
 * Resolves the user-facing error message for an auth API error.
 * Uses response + data + context to apply the right mapping (backend message, mapped reason, status, default).
 * Returns null only for 'login-email' when redirectToErrorPage and status 429 (caller should redirect, not show message).
 */
export function getAuthErrorMessage(
  response: Response,
  data: AuthResponseData | undefined,
  context: AuthErrorContext
): string | null {
  const options: ErrorMappingOptions = {
    statusCode: response.status,
    reason: data?.reason,
    message: data?.message,
    redirectToErrorPage: data?.redirectToErrorPage,
    accountAlreadyExists: data?.accountAlreadyExists,
  }

  switch (context) {
    case 'login-email':
      return getSignInErrorMessage(options)
    case 'login-password':
      return getSignInPasswordErrorMessage(options)
    case 'signup-details':
      return getSignupDetailsErrorMessage(options)
    case 'password-recovery':
      return getPasswordRecoveryErrorMessage(options)
    case 'password-reset':
      return getPasswordResetErrorMessage(options)
    default:
      return ERROR_MESSAGES.generic.default
  }
}
